import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  API_BASE_URL, BREAKPOINT, can, formatIstDate, inboxApi, PERMISSION, useAuth, useMediaQuery, type Template,
} from '@wa/shared';
import { Bubble, CategoryChip, StatusChip, languageLabel, pretty, s, shapeOf } from '@/templates/parts';

/**
 * Templates, the reference's way: one table of everything on the account, the
 * message itself as a column, and a detail drawer that opens on the row.
 *
 * It replaces "there is no Templates screen": until F162 a client could only
 * send a template, and asked PSM for anything else. The table shows the three
 * things a person comes here to check — is it approved, what does it say, when
 * was it last sent — without opening anything, which is why the preview is a
 * column and not a hover.
 *
 * Filters are client-side over one query: an account has dozens of templates,
 * not thousands, and the list is already cached for the picker.
 */
const STATUSES = ['APPROVED', 'PENDING', 'REJECTED', 'OTHER'] as const;
const CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'] as const;
const COLUMNS = [
  ['preview', 'Preview'], ['waba', 'WABA'], ['status', 'Status'], ['createdBy', 'Created by'],
  ['createdAt', 'Created on'], ['updatedAt', 'Last updated'], ['lastUsedAt', 'Last used on'],
] as const;
type Column = typeof COLUMNS[number][0];
type SortKey = 'name' | 'createdAt' | 'updatedAt';

const statusKey = (t: Template) => {
  const k = (t.status ?? '').toUpperCase();
  return k === 'APPROVED' || k === 'PENDING' || k === 'REJECTED' ? k : 'OTHER';
};
const createdOn = (t: Template) => t.authoredAt ?? t.createdAt ?? '';

export function TemplatesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = can(user, PERMISSION.templatesWrite);
  const wide = useMediaQuery(`(min-width: ${BREAKPOINT.md}px)`);

  const templates = useQuery({ queryKey: ['templates'], queryFn: inboxApi.templates });
  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels });

  const [q, setQ] = useState('');
  const [statuses, setStatuses] = useState<Set<string>>(new Set(STATUSES));
  const [categories, setCategories] = useState<Set<string>>(new Set());
  const [agents, setAgents] = useState<Set<string>>(new Set());
  const [hidden, setHidden] = useState<Set<Column>>(new Set());
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'createdAt', dir: -1 });

  const authors = useMemo(
    () => [...new Set((templates.data ?? []).map((t) => t.createdBy).filter(Boolean) as string[])].sort(),
    [templates.data]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = (templates.data ?? []).filter((t) =>
      statuses.has(statusKey(t))
      && (categories.size === 0 || categories.has((t.category ?? '').toUpperCase()))
      && (agents.size === 0 || (t.createdBy !== null && t.createdBy !== undefined && agents.has(t.createdBy)))
      && (!needle || t.name.toLowerCase().includes(needle) || (t.bodyText ?? '').toLowerCase().includes(needle)));
    const pick = (t: Template) => sort.key === 'name' ? t.name : sort.key === 'createdAt' ? createdOn(t) : (t.updatedAt ?? '');
    return rows.sort((a, b) => pick(a).localeCompare(pick(b)) * sort.dir);
  }, [templates.data, q, statuses, categories, agents, sort]);

  const selected = id ? (templates.data ?? []).find((t) => t.id === Number(id)) ?? null : null;
  // The WABA is the business, not a number on it: the tenant's name, with the
  // first channel's verified name as the fallback for a tenant that has none.
  const wabaName = (wabaId: string | undefined) =>
    user?.tenantName ?? (channels.data ?? []).find((c) => c.isActive)?.verifiedName ?? (wabaId ? `WABA ${wabaId}` : '—');
  const channelCount = (channels.data ?? []).filter((c) => c.isActive).length || 1;

  const toggleSort = (key: SortKey) =>
    setSort((cur) => ({ key, dir: cur.key === key ? (cur.dir === 1 ? -1 : 1) : (key === 'name' ? 1 : -1) }));

  const cards = !wide || view === 'grid';

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-(--color-paper)">
      <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-(--color-line) bg-(--color-surface) px-4">
        <h1 className="text-lead font-semibold">Templates</h1>
        {canWrite && (
          <Link
            to="/templates/new"
            className="tap flex h-10 items-center gap-2 rounded-(--radius-field) bg-(--color-brand) px-4 text-body font-semibold text-(--color-brand-ink) shadow-(--shadow-pop) hover:bg-(--color-brand-strong)"
          >
            <IconNew /> New Template
          </Link>
        )}
      </header>

      <div className="flex shrink-0 flex-wrap items-center gap-2 px-3 pt-3 sm:px-4">
        <label className="flex h-11 min-w-0 flex-1 basis-56 items-center gap-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-3 sm:max-w-95">
          <IconSearch />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search template.."
            aria-label="Search templates"
            className="min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-(--color-faint)"
          />
        </label>
        <CheckMenu
          label="All status" active={statuses.size < STATUSES.length ? `Status` : null} count={statuses.size < STATUSES.length ? statuses.size : 0}
          options={STATUSES.map((v) => ({ value: v, node: <StatusChip status={v === 'OTHER' ? 'other' : v} /> }))}
          value={statuses} onChange={setStatuses}
        />
        <CheckMenu
          label="All categories" active={categories.size ? 'Category' : null} count={categories.size}
          options={CATEGORIES.map((v) => ({ value: v, node: <span className="text-body">{pretty(v)}</span> }))}
          value={categories} onChange={setCategories} allLabel="All categories"
        />
        <CheckMenu
          label="All agents" active={agents.size ? 'Agents' : null} count={agents.size}
          options={authors.map((v) => ({ value: v, node: <span className="text-body">{v}</span> }))}
          value={agents} onChange={setAgents} allLabel="All agents" empty="Nobody has submitted a template from here yet."
        />
        <span className="ml-auto flex items-center gap-2">
          <SyncButton />
          {wide && (
            <CheckMenu
              label="View Options" icon={<IconEye />} align="right"
              options={COLUMNS.map(([value, label]) => ({ value, node: <span className="text-body">{label}</span> }))}
              value={new Set(COLUMNS.map(([c]) => c).filter((c) => !hidden.has(c)))}
              onChange={(next) => setHidden(new Set(COLUMNS.map(([c]) => c).filter((c) => !next.has(c))))}
              fixed={[{ node: <span className="text-body text-(--color-faint)">Template name</span> }]}
            />
          )}
          {wide && (
            <span role="group" aria-label="View" className="flex h-11 overflow-hidden rounded-(--radius-field) border border-(--color-line) bg-(--color-surface)">
              <button type="button" onClick={() => setView('grid')} aria-pressed={view === 'grid'} aria-label="Cards" className={`tap grid w-9 place-items-center ${view === 'grid' ? 'bg-(--color-brand-wash) text-(--color-brand)' : 'text-(--color-muted)'}`}><IconGrid /></button>
              <button type="button" onClick={() => setView('list')} aria-pressed={view === 'list'} aria-label="Table" className={`tap grid w-9 place-items-center border-l border-(--color-line) ${view === 'list' ? 'bg-(--color-brand-wash) text-(--color-brand)' : 'text-(--color-muted)'}`}><IconRows /></button>
            </span>
          )}
        </span>
      </div>

      <div className="pane-scroll min-h-0 flex-1 overflow-auto p-3 sm:p-4">
        {templates.isPending && <p className="py-16 text-center text-small text-(--color-muted)">Loading…</p>}
        {templates.isError && <p className="py-16 text-center text-small text-(--color-bad)">The templates could not be loaded.</p>}
        {templates.isSuccess && shown.length === 0 && (
          <div className="grid min-h-[50vh] place-items-center text-center">
            <div>
              <IconWarnBig />
              <p className="mt-2 text-body text-(--color-ink-soft)">No templates found</p>
              {templates.data.length === 0 && canWrite && (
                <Link to="/templates/new" className="mt-3 inline-block text-body font-medium text-(--color-brand) hover:underline">Create your first template</Link>
              )}
            </div>
          </div>
        )}

        {templates.isSuccess && shown.length > 0 && (cards ? (
          <ul className="flex flex-col gap-3">
            {shown.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/templates/${t.id}`)}
                  className={`tap grid w-full gap-4 rounded-(--radius-panel) border bg-(--color-surface) p-3 text-left hover:border-(--color-muted) sm:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] sm:p-4 ${selected?.id === t.id ? 'border-(--color-brand)' : 'border-(--color-line)'}`}
                >
                  <div className="chat-ground rounded-(--radius-field) p-3">
                    <Bubble shape={shapeOf(t)} width="w-full" bodyClass="line-clamp-6" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <CategoryChip category={t.category} />
                      <span className="text-small font-medium text-(--color-tick-read)">{languageLabel(t.language)}</span>
                      <span className="ml-auto"><StatusChip status={t.status} /></span>
                    </div>
                    <h2 className="mt-2 truncate text-lead font-semibold">{t.name}</h2>
                    <dl className="mt-3 grid gap-x-6 gap-y-3 border-t border-(--color-line-dim) pt-3 text-small sm:grid-cols-2">
                      <Meta icon={<IconBuilding />} label="WABA:" value={wabaName(t.wabaId)} />
                      <Meta icon={<IconPerson />} label="Created by:" value={t.createdBy ?? 'Synced from WhatsApp'} />
                      <Meta icon={<IconCalendar />} label="Created on:" value={formatIstDate(createdOn(t))} />
                      <Meta icon={<IconCalendar />} label="Last updated:" value={formatIstDate(t.updatedAt)} />
                    </dl>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="overflow-x-auto rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface)">
            <table className="w-full min-w-[60rem] border-collapse text-left whitespace-nowrap">
              <thead>
                <tr className="h-10 bg-(--color-paper) text-small text-(--color-ink-soft)">
                  <Th onSort={() => toggleSort('name')} sorted={sort.key === 'name' ? sort.dir : 0} className="w-52 pl-4">Template name</Th>
                  {!hidden.has('preview') && <Th className="w-40">Preview</Th>}
                  {!hidden.has('waba') && <Th className="w-52">WABA</Th>}
                  {!hidden.has('status') && <Th>Status</Th>}
                  {!hidden.has('createdBy') && <Th align="right" className="w-36">Created by</Th>}
                  {!hidden.has('createdAt') && <Th align="right" onSort={() => toggleSort('createdAt')} sorted={sort.key === 'createdAt' ? sort.dir : 0} className="w-32">Created on</Th>}
                  {!hidden.has('updatedAt') && <Th align="right" onSort={() => toggleSort('updatedAt')} sorted={sort.key === 'updatedAt' ? sort.dir : 0} className="w-32">Last updated</Th>}
                  {!hidden.has('lastUsedAt') && <Th align="right" className="w-32 pr-4">Last used on</Th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/templates/${t.id}`)}
                    className={`h-20 cursor-pointer border-t border-(--color-line-dim) hover:bg-(--color-sunken) ${selected?.id === t.id ? 'bg-(--color-brand-wash)' : ''}`}
                  >
                    <td className="pl-4 pr-3 align-middle">
                      <span className="block truncate text-body font-semibold">{t.name}</span>
                      <span className="mt-1.5 flex items-center gap-2">
                        <CategoryChip category={t.category} />
                        <span className="text-small text-(--color-muted)">{languageLabel(t.language)}</span>
                      </span>
                    </td>
                    {!hidden.has('preview') && (
                      <td className="pr-3 align-middle">
                        <span className="block w-36 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small leading-[1.5] text-(--color-ink-soft)">
                          <span className="line-clamp-2 whitespace-pre-line">{previewLines(t)}</span>
                        </span>
                      </td>
                    )}
                    {!hidden.has('waba') && (
                      <td className="pr-3 align-middle">
                        <span className="block truncate text-body font-semibold">{wabaName(t.wabaId)}</span>
                        <span className="mt-1.5 inline-flex items-center gap-1 rounded-(--radius-chip) bg-(--color-sunken) px-2 py-0.5 text-small font-medium text-(--color-ink-soft)">
                          <IconSignal /> {channelCount} {channelCount === 1 ? 'Channel' : 'Channels'}
                        </span>
                      </td>
                    )}
                    {!hidden.has('status') && <td className="pr-3 align-middle"><StatusChip status={t.status} /></td>}
                    {!hidden.has('createdBy') && <td className="pr-3 text-right align-middle text-small text-(--color-muted)">{t.createdBy ?? '—'}</td>}
                    {!hidden.has('createdAt') && <td className="pr-3 text-right align-middle text-small tabular-nums">{formatIstDate(createdOn(t))}</td>}
                    {!hidden.has('updatedAt') && <td className="pr-3 text-right align-middle text-small tabular-nums">{formatIstDate(t.updatedAt)}</td>}
                    {!hidden.has('lastUsedAt') && <td className="pr-4 text-right align-middle text-small tabular-nums">{formatIstDate(t.lastUsedAt, '-')}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {selected && (
        <DetailsDrawer
          template={selected}
          wabaName={wabaName(selected.wabaId)}
          channels={(channels.data ?? []).filter((c) => c.isActive)}
          canWrite={canWrite}
          wide={wide}
          onClose={() => navigate('/templates')}
        />
      )}
    </div>
  );
}

/** The table's preview: the header if there is one, then the body, cut to two lines. */
function previewLines(t: Template): string {
  const lines = [t.headerText, ...(t.bodyText ?? '').split('\n')].filter((l): l is string => !!l && l.trim() !== '');
  return lines.length > 1 ? `${lines[0]}\n${lines.slice(1).join(' ')}` : `${lines[0] ?? ''}\n…`;
}

function Meta({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="grid size-8 shrink-0 place-items-center rounded-full bg-(--color-brand-wash) text-(--color-brand)">{icon}</span>
      <span className="min-w-0">
        <dt className="text-small text-(--color-muted)">{label}</dt>
        <dd className="truncate text-body font-medium">{value}</dd>
      </span>
    </div>
  );
}

function Th({ children, onSort, sorted = 0, align, className = '' }: {
  children: React.ReactNode; onSort?: () => void; sorted?: number; align?: 'right'; className?: string;
}) {
  const inner = (
    <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
      {children}
      {onSort && (
        <svg viewBox="0 0 20 20" className={`size-3.5 ${sorted ? 'text-(--color-ink)' : 'text-(--color-faint)'}`} aria-hidden="true">
          {sorted === 0 && <path d="m7 8.5 3-3 3 3M7 11.5l3 3 3-3" {...s} />}
          {sorted === 1 && <path d="m6.5 12 3.5-4 3.5 4" {...s} />}
          {sorted === -1 && <path d="m6.5 8 3.5 4 3.5-4" {...s} />}
        </svg>
      )}
    </span>
  );
  return (
    <th scope="col" aria-sort={sorted === 1 ? 'ascending' : sorted === -1 ? 'descending' : undefined}
      className={`border-r border-(--color-line-dim) pr-3 font-medium last:border-r-0 ${align === 'right' ? 'text-right' : ''} ${className}`}>
      {onSort ? <button type="button" onClick={onSort} className="tap w-full text-inherit">{inner}</button> : inner}
    </th>
  );
}

/**
 * A filter as the reference draws it: a bordered button naming the whole set
 * until something is chosen, then the noun with a count. The list is checkboxes,
 * because a person filtering templates wants "pending and rejected", which a
 * select cannot say.
 */
function CheckMenu<T extends string>({ label, active, count = 0, icon, options, value, onChange, allLabel, fixed, empty, align }: {
  label: string; active?: string | null; count?: number; icon?: React.ReactNode;
  options: { value: T; node: React.ReactNode }[]; value: Set<T>; onChange: (next: Set<T>) => void;
  allLabel?: string; fixed?: { node: React.ReactNode }[]; empty?: string; align?: 'right';
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away); document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', key); };
  }, [open]);
  const toggle = (v: T) => {
    const next = new Set(value);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(next);
  };
  const all = allLabel !== undefined;
  return (
    <div ref={box} className="relative">
      <button
        type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="true"
        className={`tap flex h-11 items-center gap-1.5 rounded-(--radius-field) border bg-(--color-surface) px-3 text-body font-medium text-(--color-ink-soft) ${open ? 'border-(--color-brand)' : 'border-(--color-line)'}`}
      >
        {icon}{active ?? label}
        {count > 0 && <span className="grid size-5 place-items-center rounded-full bg-(--color-brand) text-micro font-semibold text-(--color-brand-ink)">{count}</span>}
        <svg viewBox="0 0 20 20" className={`size-4 text-(--color-muted) transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true"><path d="m6 8 4 4 4-4" {...s} /></svg>
      </button>
      {open && (
        <ul className={`absolute top-full z-30 mt-1 min-w-44 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-pop) ${align === 'right' ? 'right-0' : 'left-0'}`}>
          {fixed?.map((f, i) => (
            <li key={`f${i}`} className="flex items-center gap-2.5 px-3 py-2 opacity-70"><span className="grid size-4.5 place-items-center rounded border border-(--color-line) bg-(--color-sunken)"><IconTick /></span>{f.node}</li>
          ))}
          {all && (
            <li>
              <button type="button" onClick={() => onChange(new Set())} className="tap flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-(--color-sunken)">
                <Box checked={value.size === 0} /><span className="text-body">{allLabel}</span>
              </button>
            </li>
          )}
          {options.length === 0 && <li className="px-3 py-2 text-small text-(--color-muted)">{empty}</li>}
          {options.map((o) => (
            <li key={o.value} className={all ? 'border-t border-(--color-line-dim)' : ''}>
              <button type="button" onClick={() => toggle(o.value)} className="tap flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-(--color-sunken)">
                <Box checked={value.has(o.value)} />{o.node}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Box({ checked }: { checked: boolean }) {
  return (
    <span aria-hidden="true" className={`grid size-4.5 shrink-0 place-items-center rounded border ${checked ? 'border-(--color-brand) bg-(--color-brand) text-(--color-brand-ink)' : 'border-(--color-line) bg-(--color-surface)'}`}>
      {checked && <IconTick />}
    </span>
  );
}

function SyncButton() {
  const queryClient = useQueryClient();
  const sync = useMutation({
    mutationFn: inboxApi.syncTemplates,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['templates'] }),
  });
  return (
    <button
      type="button" onClick={() => sync.mutate()} disabled={sync.isPending} title="Refresh from WhatsApp" aria-label="Refresh from WhatsApp"
      className="tap grid size-11 place-items-center rounded-(--radius-field) text-(--color-ink-soft) hover:bg-(--color-surface) disabled:opacity-60"
    >
      <svg viewBox="0 0 20 20" className={`size-5 ${sync.isPending ? 'animate-spin' : ''}`} aria-hidden="true"><path d="M16 9A6 6 0 0 0 5.2 6.2M4 11a6 6 0 0 0 10.8 2.8" {...s} /><path d="M16 4.5V9h-4.5M4 15.5V11h4.5" {...s} /></svg>
    </button>
  );
}

/**
 * The drawer the row opens. The reference's "WABA level breakdown": the same
 * template can exist on several accounts with a status on each; this platform
 * keys templates by WABA already, so the table has one row per channel that
 * shares it. The bubble floats beside the drawer, as theirs does, so the message
 * and its facts are on screen together.
 */
function DetailsDrawer({ template: t, wabaName, channels, canWrite, wide, onClose }: {
  template: Template; wabaName: string; channels: { id: number; verifiedName: string | null; displayPhoneNumber: string }[];
  canWrite: boolean; wide: boolean; onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [menu, setMenu] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [api, setApi] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { if (menu) setMenu(false); else if (confirm) setConfirm(false); else if (api) setApi(false); else onClose(); } };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [menu, confirm, api, onClose]);
  useEffect(() => {
    if (!menu) return;
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [menu]);

  const remove = useMutation({
    mutationFn: () => inboxApi.deleteTemplate(t.id),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['templates'] }); onClose(); },
  });

  const editable = ['APPROVED', 'REJECTED', 'PAUSED'].includes((t.status ?? '').toUpperCase());

  return (
    <>
      {!wide && <div className="fixed inset-0 z-30 bg-black/40" onClick={onClose} />}
      {wide && (
        <div className="pointer-events-none absolute top-16 right-[41rem] z-40 hidden w-80 lg:block">
          <div className="chat-ground rounded-(--radius-panel) p-4 shadow-(--shadow-pop)">
            <Bubble shape={shapeOf(t)} width="w-full" />
          </div>
        </div>
      )}
      <aside
        role="dialog" aria-modal={!wide} aria-label="Template details"
        className={`fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-(--color-line) bg-(--color-surface) shadow-(--shadow-pop) sm:w-[min(40rem,100vw)] ${wide ? 'md:absolute md:inset-y-0 md:shadow-none' : ''}`}
      >
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-(--color-line) px-4">
          <span className="text-body font-semibold">Template details</span>
          <span className="text-(--color-faint)" aria-hidden="true">›</span>
          <span className="truncate text-body text-(--color-muted)">WABA level breakdown</span>
          <button type="button" onClick={onClose} aria-label="Close" className="tap ml-auto grid size-9 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken)">
            <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" {...s} /></svg>
          </button>
        </header>

        <div className="pane-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <div className="flex items-start gap-2">
            <h2 className="min-w-0 flex-1 truncate text-lead font-semibold">{t.name}</h2>
            <div ref={box} className="relative">
              <button type="button" onClick={() => setMenu((m) => !m)} aria-label="More actions" aria-expanded={menu} className="tap grid size-9 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)">
                <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><circle cx="10" cy="4.5" r="1.3" fill="currentColor" /><circle cx="10" cy="10" r="1.3" fill="currentColor" /><circle cx="10" cy="15.5" r="1.3" fill="currentColor" /></svg>
              </button>
              {menu && (
                <ul role="menu" className="absolute top-full right-0 z-10 mt-1 w-44 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-pop)">
                  {canWrite && (
                    <MenuItem icon={<IconEdit />} label="Edit Template" disabled={!editable}
                      title={editable ? undefined : 'Wait for WhatsApp to finish reviewing it'}
                      onClick={() => navigate(`/templates/${t.id}/edit`)} />
                  )}
                  {canWrite && <MenuItem icon={<IconDuplicate />} label="Duplicate" onClick={() => navigate(`/templates/new?from=${t.id}`)} />}
                  <MenuItem icon={<IconCode />} label="Send via API" onClick={() => { setMenu(false); setApi(true); }} />
                  {canWrite && <MenuItem icon={<IconTrash />} label="Delete" danger onClick={() => { setMenu(false); setConfirm(true); }} />}
                </ul>
              )}
            </div>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-small text-(--color-muted)">
            <span className="rounded-(--radius-chip) bg-(--color-sunken) px-2 py-0.5 text-small font-medium text-(--color-ink-soft)">{pretty(t.category ?? 'unknown')}</span>
            <span className="inline-flex items-center gap-1"><IconPerson /> {t.createdBy ?? 'WhatsApp'}</span>
            <span aria-hidden="true">•</span><span>{languageLabel(t.language)}</span>
            <span aria-hidden="true">•</span><span>{formatIstDate(createdOn(t))}</span>
          </div>

          {t.rejectedReason && (
            <p className="mt-3 rounded-(--radius-field) border border-(--color-bad)/40 bg-(--color-bad)/8 px-3 py-2 text-small text-(--color-bad)">
              <span className="font-semibold">Rejected by WhatsApp:</span> {t.rejectedReason}
            </p>
          )}

          <div className="chat-ground mt-4 rounded-(--radius-field) p-3 lg:hidden">
            <Bubble shape={shapeOf(t)} width="w-full" />
          </div>

          <div className="mt-4 overflow-x-auto rounded-(--radius-field) border border-(--color-line)">
            <table className="w-full min-w-[26rem] border-collapse text-left">
              <thead>
                <tr className="h-10 bg-(--color-paper) text-small text-(--color-ink-soft)">
                  <th className="border-r border-(--color-line-dim) pl-3 font-medium">WABA</th>
                  <th className="border-r border-(--color-line-dim) pl-3 font-medium">Channels</th>
                  <th className="pl-3 font-medium">Status</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {(channels.length ? channels : [{ id: 0, verifiedName: null, displayPhoneNumber: '' }]).map((c) => (
                  <tr key={c.id} className="h-14 border-t border-(--color-line-dim)">
                    <td className="pl-3 text-body font-medium">{wabaName}</td>
                    <td className="pl-3">
                      <span className="flex items-center gap-1.5 text-small text-(--color-ink-soft)">
                        <IconSignal />
                        <span><span className="block">{c.verifiedName ?? wabaName}</span><span className="block text-(--color-muted)">{c.displayPhoneNumber}</span></span>
                      </span>
                    </td>
                    <td className="pl-3"><StatusChip status={t.status} /></td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {t.syncedAt && <p className="mt-3 text-micro text-(--color-faint)">Last checked with WhatsApp {formatIstDate(t.syncedAt)}.</p>}
        </div>

        {confirm && (
          <div className="absolute inset-0 z-20 grid place-items-center bg-black/30 p-4" onClick={() => setConfirm(false)}>
            <div role="alertdialog" aria-label="Delete template" onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-(--radius-panel) bg-(--color-surface) p-4 shadow-(--shadow-pop)">
              <h3 className="text-lead font-semibold">Delete {t.name}?</h3>
              <p className="mt-1.5 text-body text-(--color-muted)">
                It is removed from WhatsApp for every language, and a broadcast that uses it cannot be sent again. Messages already sent keep their history.
              </p>
              {remove.isError && <p role="alert" className="mt-2 text-small text-(--color-bad)">{remove.error instanceof Error ? remove.error.message : 'It could not be deleted.'}</p>}
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setConfirm(false)} className="tap rounded-(--radius-field) px-3 py-2 text-body font-medium text-(--color-muted) hover:bg-(--color-sunken)">Cancel</button>
                <button type="button" disabled={remove.isPending} onClick={() => remove.mutate()} className="tap rounded-(--radius-field) bg-(--color-bad) px-4 py-2 text-body font-semibold text-white hover:opacity-90 disabled:opacity-60">
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {api && <SendViaApi template={t} onClose={() => setApi(false)} />}
      </aside>
    </>
  );
}

function MenuItem({ icon, label, onClick, danger, disabled, title }: {
  icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean; disabled?: boolean; title?: string;
}) {
  return (
    <li role="none">
      <button type="button" role="menuitem" onClick={onClick} disabled={disabled} title={title}
        className={`tap flex w-full items-center gap-2 px-3 py-2 text-left text-body hover:bg-(--color-sunken) disabled:opacity-50 ${danger ? 'text-(--color-bad)' : 'text-(--color-ink)'}`}>
        {icon}{label}
      </button>
    </li>
  );
}

/** The reference's "Send via API": the call, filled in for this template, ready to paste. */
function SendViaApi({ template: t, onClose }: { template: Template; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const vars = Array.from({ length: t.bodyVariables }, (_, i) => `"value ${i + 1}"`).join(', ');
  const snippet = `curl -X POST ${API_BASE_URL}/api/v1/messages/template \\
  -H "Authorization: Bearer <your API key>" \\
  -H "Content-Type: application/json" \\
  -d '{ "conversationId": <conversation id>, "templateId": ${t.id}, "variables": [${vars}] }'`;
  return (
    <div className="absolute inset-0 z-20 grid place-items-center bg-black/30 p-4" onClick={onClose}>
      <div role="dialog" aria-label="Send via API" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-(--radius-panel) bg-(--color-surface) p-4 shadow-(--shadow-pop)">
        <h3 className="text-lead font-semibold">Send via API</h3>
        <p className="mt-1 text-small text-(--color-muted)">
          Template <span className="font-medium text-(--color-ink)">{t.name}</span> is id {t.id}. Keys and webhooks live under Settings › API &amp; webhooks.
        </p>
        <pre className="pane-scroll mt-3 overflow-x-auto rounded-(--radius-field) bg-(--color-paper) p-3 text-micro leading-relaxed whitespace-pre">{snippet}</pre>
        <div className="mt-3 flex justify-end gap-2">
          <Link to="/settings/developer" className="tap rounded-(--radius-field) px-3 py-2 text-body font-medium text-(--color-brand) hover:bg-(--color-sunken)">API settings</Link>
          <button type="button" onClick={() => { void navigator.clipboard?.writeText(snippet); setCopied(true); }} className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong)">
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

const IconNew = () => <svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><rect x="3" y="5" width="14" height="11" rx="1.8" {...s} /><path d="m3 6.8 7 4.4 7-4.4" {...s} /></svg>;
const IconSearch = () => <svg viewBox="0 0 20 20" className="size-4.5 shrink-0 text-(--color-muted)" aria-hidden="true"><circle cx="8.8" cy="8.8" r="5.2" {...s} /><path d="m12.8 12.8 3.6 3.6" {...s} /></svg>;
const IconEye = () => <svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><path d="M2.5 10s2.8-5 7.5-5 7.5 5 7.5 5-2.8 5-7.5 5-7.5-5-7.5-5Z" {...s} /><circle cx="10" cy="10" r="2.3" {...s} /></svg>;
const IconGrid = () => <svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><rect x="3.5" y="3.5" width="5.2" height="5.2" rx="1" {...s} /><rect x="11.3" y="3.5" width="5.2" height="5.2" rx="1" {...s} /><rect x="3.5" y="11.3" width="5.2" height="5.2" rx="1" {...s} /><rect x="11.3" y="11.3" width="5.2" height="5.2" rx="1" {...s} /></svg>;
const IconRows = () => <svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><path d="M4 5.5h12M4 10h12M4 14.5h12" {...s} /></svg>;
const IconTick = () => <svg viewBox="0 0 20 20" className="size-3.5" aria-hidden="true"><path d="m5 10.5 3.2 3L15 6.5" {...s} strokeWidth={2} /></svg>;
const IconSignal = () => <svg viewBox="0 0 20 20" className="size-3.5 shrink-0" aria-hidden="true"><circle cx="10" cy="10" r="1.6" {...s} /><path d="M6.8 6.8a4.5 4.5 0 0 0 0 6.4M13.2 6.8a4.5 4.5 0 0 1 0 6.4M4.6 4.6a7.6 7.6 0 0 0 0 10.8M15.4 4.6a7.6 7.6 0 0 1 0 10.8" {...s} /></svg>;
const IconBuilding = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M4 17V4.5h8V17M12 8.5h4V17M6.5 7.5h3M6.5 10.5h3M6.5 13.5h3M3 17h14" {...s} /></svg>;
const IconPerson = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><circle cx="10" cy="6.6" r="2.9" {...s} /><path d="M4.4 16.4a5.8 5.8 0 0 1 11.2 0" {...s} /></svg>;
const IconCalendar = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><rect x="3.5" y="4.5" width="13" height="12" rx="1.6" {...s} /><path d="M3.5 8.5h13M7 3v3M13 3v3" {...s} /></svg>;
const IconEdit = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M4 16h3l8-8-3-3-8 8zM11 6l3 3" {...s} /></svg>;
const IconDuplicate = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><rect x="7" y="7" width="9" height="9" rx="1.5" {...s} /><path d="M13 7V5.5A1.5 1.5 0 0 0 11.5 4h-6A1.5 1.5 0 0 0 4 5.5v6A1.5 1.5 0 0 0 5.5 13H7" {...s} /></svg>;
const IconCode = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="m7 6-4 4 4 4M13 6l4 4-4 4M11.5 4l-3 12" {...s} /></svg>;
const IconTrash = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M4 6h12M8 6V4h4v2M6 6l.8 10h6.4L14 6" {...s} /></svg>;
const IconWarnBig = () => <svg viewBox="0 0 24 24" className="mx-auto size-8 text-(--color-ink-soft)" aria-hidden="true"><path d="M12 3 22 20H2z" fill="currentColor" /><path d="M12 9v5M12 16.5v.3" stroke="var(--color-surface)" strokeWidth="1.8" strokeLinecap="round" /></svg>;
